/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate } from "react-router";
import { ReactComponent as LeftArrow } from "../../assets/icons/arrows/left-arrow.svg";
import {
  ControlledInput,
  ControlledSelect,
  FormUpload,
  PrimaryButton,
  SecondaryButton,
} from "../../components";
import { useEffect, useState } from "react";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useUsers } from "../../store/useUsers";
import { useForm } from "react-hook-form";
import { useAuth } from "../../store/useAuth";
import { useUploadImage } from "../../store/useUploadImage";

type Inputs = {
  email: string;
  first_name: string;
  last_name?: string;
  phone_number?: string;
  user_role?: "1" | "2" | "3" | "4";
  location?: string;
};

function ProfileScreen() {
  /** Hooks */
  const { user, getUser } = useAuth();
  const { updateUser, clearUpdatedUser, updatedUser } = useUsers();
  const navigate = useNavigate();
  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();
  const { uploadImage, clearUploadedImage, uploadedImage, setUploadedImage } =
    useUploadImage();
  /** State */
  const [image, setImage] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (user) {
      reset({
        email: user.email as string,
        first_name: user.first_name as string,
        last_name: user.last_name as string,
        phone_number: user.phone_number as string,
        user_role: user.user_role as any,
        location: user.location as string,
      });
      setUploadedImage(user?.profileImage as string);
    }
  }, [user]);

  useEffect(() => {
    if (updatedUser) {
      getUser();
    }
  }, [updatedUser]);

  useEffect(() => {
    return () => {
      clearUpdatedUser();
      clearUploadedImage();
    };
  }, []);

  /** Callbacks */
  const goBack = () => navigate(-1);
  // const onSubmit = () => updateUserRole(params?.id as string, userRole);
  const onSubmit = (data: Inputs) => {
    if (!uploadedImage) {
      return setSubmitted(true);
    }
    updateUser(user?._id as string, {
      ...data,
      profileImage: uploadedImage,
      user_role: Number(data?.user_role),
    });
  };

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setImage(e.target.files[0]);
    }
  };

  useEffect(() => {
    if (image) {
      onUploadImage();
    }
  }, [image]);

  const onUploadImage = () => {
    if (!image) return;
    uploadImage(image);
  };

  const imageError = submitted && !image;

  return (
    <div>
      <div className="flex flex-row items-center mb-6">
        <LeftArrow onClick={goBack} className="mr-3 cursor-pointer" />
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          {user?.first_name} {user?.last_name}
        </h1>
      </div>
      <div className="flex flex-row">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full sm:w-1/2 min-w-fit p-7 bg-white  border-gray-border drop-shadow-none rounded-xl"
        >
          {/* Email */}
          <ControlledInput
            control={control}
            error={Boolean(errors?.first_name)}
            label="First name"
            placeholder="Enter first name here..."
            name="first_name"
            rules={{ required: true }}
          />
          <ControlledInput
            control={control}
            error={Boolean(errors?.last_name)}
            label="Last name"
            placeholder="Enter last name here..."
            name="last_name"
            rules={{ required: false }}
          />
          <ControlledInput
            control={control}
            error={Boolean(errors?.email)}
            label="Email"
            placeholder="Enter email here..."
            name="email"
            type="email"
            rules={{ required: true, pattern: /^\S+@\S+$/i }}
          />
          <ControlledInput
            control={control}
            error={Boolean(errors?.phone_number)}
            label="Phone number"
            placeholder="Enter phone number here..."
            name="phone_number"
            type="tel"
            rules={{ required: false }}
          />
          <ControlledInput
            control={control}
            error={Boolean(errors?.location)}
            label="Location"
            placeholder="Enter location here..."
            name="location"
            rules={{ required: false }}
          />
          {user?.user_role === 7 && (
            <ControlledSelect
              name="user_role"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.user_role)}
              label="User role"
              options={[
                { _id: "1", title: "Admin" },
                { _id: "5", title: "Store Admin" },
                { _id: "2", title: "Manager" },
                { _id: "3", title: "User" },
                { _id: "7", title: "Super Admin" },
              ]}
              placeholder="Pick user role here"
            />
          )}
          <FormUpload
            label="Profile picture"
            onChange={onImageChange}
            roundedImage
            value={image}
            error={imageError}
            imageUrl={uploadedImage as string}
          />
          <div className="flex flex-row items-center justify-end">
            <SecondaryButton type="button" onClick={goBack} label="Cancel" />
            <PrimaryButton type="submit" className="ml-4" label="Save" />
          </div>
        </form>
      </div>
    </div>
  );
}

export default withProtectedRoute(ProfileScreen);
